// utils/qocCapCommitmentExcel.js
// Offline export/import for the CAP Commitment popup. One row per finding
// (new findings from today's assessment + existing still-open items for
// the facility), with root cause captured as typed letter(s) against a
// legend column — same pattern as the IPCAF offline workflow.

import ExcelJS from 'exceljs';

export const ROOT_CAUSES = [
    'Policy gap',
    'Staff knowledge / training',
    'Infrastructure / equipment',
    'Resource / supply shortage',
    'Process / workflow issue',
    'Supervision gap',
    'Documentation failure',
    'Other',
];
const ROOT_CAUSE_KEYS = ROOT_CAUSES.map((_, i) => String.fromCharCode(97 + i)); // a..h
const rootCauseLegend = () => ROOT_CAUSES.map((rc, i) => `${ROOT_CAUSE_KEYS[i]}=${rc}`).join('\n');

const STATUS_OPTIONS = { o: 'open', i: 'in_progress', c: 'closed', x: 'cancelled' };
const statusLegend = () => Object.entries(STATUS_OPTIONS).map(([k, v]) => `${k}=${v}`).join('\n');
const statusToKey = (status) => Object.entries(STATUS_OPTIONS).find(([, v]) => v === status)?.[0] || 'o';
const keyToStatus = (key) => STATUS_OPTIONS[key] || null;

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1890FF' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };

// rows: unified array — each is either a "new finding" (has me_id, no cap_id)
// or an "existing open item" (has cap_id). See buildRowsForExport in the modal.
export async function exportCapCommitmentExcel({ facility, rows, exportedBy }) {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'QoC Platform';
    workbook.created = new Date();

    const sheet = workbook.addWorksheet('CAP Commitment', { views: [{ state: 'frozen', ySplit: 1 }] });
    sheet.columns = [
        { header: 'CAP ID',            key: 'cap_id',   width: 10 },
        { header: 'ME ID',             key: 'me_id',    width: 10 },
        { header: 'Ref No',            key: 'ref_no',   width: 10 },
        { header: 'Clause',            key: 'clause',   width: 14 },
        { header: 'Finding',           key: 'finding',  width: 45 },
        { header: 'Checklist Gaps (reference only — do not edit)', key: 'gaps', width: 45 },
        { header: 'Root Cause Legend', key: 'rc_legend',width: 35 },
        { header: 'Root Cause Keys (type letters, e.g. b,d)', key: 'rc_keys', width: 22 },
        { header: 'Corrective Action', key: 'action',   width: 45 },
        { header: 'Responsible Person',key: 'responsible', width: 22 },
        { header: 'Target Date (YYYY-MM-DD)', key: 'target_date', width: 20 },
        { header: `Status (${statusLegend().replace(/\n/g, '; ')})`, key: 'status', width: 25 },
    ];
    sheet.getRow(1).eachCell(cell => { cell.fill = HEADER_FILL; cell.font = HEADER_FONT; });

    rows.forEach(r => {
        const row = sheet.addRow({
            cap_id: r.cap_id || '',
            me_id: r.me_id || '',
            ref_no: r.ref_no || '',
            clause: r.clause_code || '',
            finding: r.finding || '',
            gaps: (r.checklist_gaps || []).join('; '),
            rc_legend: rootCauseLegend(),
            rc_keys: (r.root_cause_keys || []).join(','),
            action: r.action_description || '',
            responsible: r.responsible_person || '',
            target_date: r.target_date || '',
            status: statusToKey(r.status || 'open'),
        });
        row.getCell('finding').alignment = { wrapText: true };
        row.getCell('gaps').alignment = { wrapText: true };
        row.getCell('rc_legend').alignment = { wrapText: true };
        row.getCell('ref_no').numFmt = '@';
    });

    const meta = workbook.addWorksheet('Meta');
    meta.state = 'veryHidden';
    meta.addRows([
        ['key', 'value'],
        ['facility_id', facility?.id || ''],
        ['facility_name', facility?.business_name || ''],
        ['exported_at', new Date().toISOString()],
        ['exported_by', exportedBy || ''],
    ]);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filename = `${(facility?.business_name || 'Facility').replace(/[^\w\- ]/g, '')}_CAP_Commitment_${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return filename;
}

export async function importCapCommitmentExcel(file) {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const meta = {};
    const metaSheet = workbook.getWorksheet('Meta');
    if (metaSheet) {
        metaSheet.eachRow((row, rowNum) => {
            if (rowNum === 1) return;
            const key = row.getCell(1).value;
            const val = row.getCell(2).value;
            if (key) meta[key] = val;
        });
    }

    const sheet = workbook.getWorksheet('CAP Commitment');
    const rows = [];
    const errors = [];
    if (sheet) {
        sheet.eachRow((row, rowNum) => {
            if (rowNum === 1) return;
            const capId  = row.getCell(1).value;
            const meId   = row.getCell(2).value;
            const refNo  = row.getCell(3).value;
            if (!capId && !meId) return; // blank row

            const finding      = row.getCell(5).value || '';
            const rcKeysRaw    = row.getCell(8).value;
            const action       = row.getCell(9).value || '';
            const responsible  = row.getCell(10).value || '';
            const targetDate   = row.getCell(11).value;
            const statusKeyRaw = row.getCell(12).value;

            const rcKeys = rcKeysRaw ? String(rcKeysRaw).split(',').map(s => s.trim()).filter(Boolean) : [];
            const invalidRc = rcKeys.filter(k => !ROOT_CAUSE_KEYS.includes(k));
            if (invalidRc.length > 0) {
                errors.push(`Row for ${refNo || capId}: invalid root cause key(s) "${invalidRc.join(',')}"`);
            }
            const validRcKeys = rcKeys.filter(k => ROOT_CAUSE_KEYS.includes(k));
            const rootCauses = validRcKeys.map(k => ROOT_CAUSES[ROOT_CAUSE_KEYS.indexOf(k)]);

            let status = null;
            if (statusKeyRaw) {
                const key = String(statusKeyRaw).trim().toLowerCase();
                status = keyToStatus(key);
                if (!status) errors.push(`Row for ${refNo || capId}: invalid status key "${statusKeyRaw}"`);
            }

            let targetDateStr = null;
            if (targetDate) {
                if (targetDate instanceof Date) {
                    targetDateStr = targetDate.toISOString().slice(0, 10);
                } else {
                    targetDateStr = String(targetDate).trim();
                }
            }

            rows.push({
                cap_id: capId || null,
                me_id: meId || null,
                ref_no: refNo || null,
                finding: String(finding),
                root_cause: rootCauses.join(', '),
                action_description: String(action),
                responsible_person: String(responsible),
                target_date: targetDateStr,
                status,
            });
        });
    }

    return { rows, meta, errors };
}